import type { MRT_ColumnDef, MRT_TableOptions } from 'material-react-table';
import { MRT_Localization_ES } from 'material-react-table/locales/es';

/**
 * Estilos comunes para las celdas del cuerpo de la tabla.
 * Aplicables a todas las columnas de MaterialReactTable.
 */
export const commonCellStyles = {
  px: 2,
  py: 1.5,
  fontSize: '0.875rem',
  m: '0px',
  maxWidth: 'max-content',
  minWidth: '0px',
  textAlign: 'center' as const,
  whiteSpace: 'nowrap' as const,
  color: '#4b5563',
};

/**
 * Estilos comunes para las celdas del encabezado de la tabla.
 * Aplicables a todas las columnas de MaterialReactTable.
 */
export const commonHeaderStyles = {
  align: 'center' as const,
  sx: {
    ...commonCellStyles,
    fontSize: '0.9rem',
    fontWeight: 600,
    backgroundColor: '#f8f9fa',
    color: '#374151',
    borderBottom: '2px solid #e5e7eb',
    '& .MuiTableCell-root': { textAlign: 'center' },
    '& > div': { justifyContent: 'center' },
  },
};

/**
 * Celdas de columnas con nombres o textos potencialmente largos (salto de línea).
 */
export const longTextColumnBodySx = {
  ...commonCellStyles,
  whiteSpace: 'normal' as const,
  wordBreak: 'break-word' as const,
  overflowWrap: 'break-word' as const,
  lineHeight: 1.35,
};

/**
 * Encabezado de columnas con texto largo (mantiene estilos comunes del header).
 */
export const longTextColumnHeadCellProps = {
  align: 'center' as const,
  sx: {
    ...commonHeaderStyles.sx,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
    overflowWrap: 'break-word' as const,
    lineHeight: 1.35,
  },
};

/**
 * Helper para crear columnas de MaterialReactTable con estilos comunes aplicados.
 *
 * @template T - Tipo de datos de la fila de la tabla
 * @param {keyof T} accessorKey - Clave del campo en el objeto de datos
 * @param {string} header - Texto del encabezado de la columna
 * @param {MRT_ColumnDef<T>['Cell']} Cell - Componente o función para renderizar el contenido de la celda
 * @param {number} [size=50] - Tamaño de la columna
 * @returns {MRT_ColumnDef<T>} Definición de columna con estilos comunes aplicados
 *
 * @example
 * ```tsx
 * const columns = [
 *   createTableColumn<MyDataType>('nombre', 'Nombre', ({ row }) => row.original.nombre),
 *   createTableColumn<MyDataType>('estatus', 'Estatus', ({ row }) => (
 *     <CustomSwitch checked={row.original.estatus} />
 *   )),
 * ];
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createTableColumn<T extends Record<string, any>>(
  accessorKey: keyof T,
  header: string,
  Cell: MRT_ColumnDef<T>['Cell'],
  size = 50
): MRT_ColumnDef<T> {
  return {
    accessorKey: accessorKey as string,
    header,
    size,
    Cell,
    muiTableBodyCellProps: { sx: commonCellStyles },
    muiTableHeadCellProps: commonHeaderStyles,
  };
}

/**
 * Configuración común para el campo de búsqueda de MaterialReactTable.
 */
export const commonSearchTextFieldProps = {
  size: 'small' as const,
  variant: 'outlined' as const,
};

/**
 * Configuración común para el contenedor de papel de MaterialReactTable.
 */
export const commonTablePaperProps = {
  sx: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
};

/**
 * Configuracion comun para el contenedor scrolleable de MaterialReactTable.
 * Mantiene fijo el encabezado y permite scroll solo en los registros.
 */
export const commonTableContainerProps = {
  sx: {
    flex: '1 1 auto',
    minHeight: 0,
    height: '100%',
    overflowY: 'auto',
    overflowX: 'auto',
  },
};

/**
 * Configuración común para los estilos de la tabla de MaterialReactTable.
 */
export const commonTableProps = {
  sx: {
    minWidth: 'max-content',
    '& .MuiTableBody-root': {
      '& .MuiTableRow-root': {
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        '&:nth-of-type(even)': {
          backgroundColor: '#f9fafb',
        },
        '&:hover': {
          backgroundColor: '#f3f4f6',
          transition: 'background-color 0.2s ease',
        },
      },
    },
    '& .MuiTableHead-root': {
      '& .MuiTableRow-root': {
        backgroundColor: '#f8f9fa',
      },
    },
  },
};

/**
 * Configuración común para la paginación de MaterialReactTable.
 */
export const commonPaginationProps = {
  color: 'primary' as const,
  rowsPerPageOptions: [10, 20, 25] as number[],
  shape: 'rounded' as const,
  variant: 'outlined' as const,
  sx: {
    backgroundColor: 'transparent',
    '& .MuiPaginationItem-root': {
      borderRadius: '0.5rem',
      borderColor: '#e5e7eb',
      color: '#4b5563',
      '&:hover': {
        backgroundColor: '#f3f4f6',
        borderColor: '#9ca3af',
      },
      '&.Mui-selected': {
        backgroundColor: 'background-primary',
        color: 'background-secondary',
        borderColor: 'background-primary',
        '&:hover': {
          backgroundColor: 'background-secondary',
        },
      },
    },
  },
};

/**
 * Configuración común para la columna de acciones de MaterialReactTable.
 */
export const commonActionsColumnProps = {
  header: 'Acciones',
  minSize: 200,
  size: 220,
  maxSize: 320,
  muiTableHeadCellProps: {
    align: 'center' as const,
    sx: {
      backgroundColor: '#f8f9fa',
      borderBottom: '2px solid #e5e7eb',
      fontWeight: 600,
      color: '#374151',
      whiteSpace: 'nowrap',
      minWidth: 200,
      overflow: 'visible',
      textOverflow: 'clip',
    },
  },
  muiTableBodyCellProps: {
    align: 'center' as const,
    sx: {
      minWidth: 200,
      whiteSpace: 'nowrap',
      overflow: 'visible',
    },
  },
};

/**
 * Opciones comunes de configuración base para MaterialReactTable.
 * Estas opciones se aplican a todas las tablas del sistema.
 *
 * @template TData - Tipo de datos de las filas de la tabla
 * @returns {Partial<MRT_TableOptions<TData>>} Configuración parcial de MaterialReactTable con opciones comunes
 *
 * @example
 * ```tsx
 * const table = useMaterialReactTable({
 *   ...getCommonTableOptions(),
 *   columns,
 *   data,
 *   renderRowActions: ({ row }) => <MyActions row={row} />,
 * });
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getCommonTableOptions<TData extends Record<string, any>>(): Partial<MRT_TableOptions<TData>> {
  return {
    initialState: {
      showGlobalFilter: false,
    },
    enableStickyHeader: true,
    enableRowActions: true,
    enableColumnActions: false,
    enableSorting: false,
    enableFacetedValues: true,
    positionActionsColumn: 'last',
    paginationDisplayMode: 'pages',
    localization: MRT_Localization_ES,
    muiSearchTextFieldProps: commonSearchTextFieldProps,
    muiTablePaperProps: commonTablePaperProps,
    muiTableContainerProps: commonTableContainerProps,
    muiTableProps: commonTableProps,
    muiPaginationProps: commonPaginationProps,
    displayColumnDefOptions: {
      'mrt-row-actions': commonActionsColumnProps,
    },
  };
}

